import { atomWithStorage } from "jotai/utils";
import { SelectedCartItem, SplitPaymentStates } from "../utils/interfaces";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import usePaymentLedgerHook from "../hooks/usePaymentLedgerHook";

const initialSplitState: SplitPaymentStates = {
  splitCount: 2,
  splitType: "custom",
  paymentLedgers: [],
  activeIndex: 0,
};

const STORAGE_KEY = "splits";
const getInitialStoredSplitPaymentState = (): Record<
  number,
  SplitPaymentStates
> => {
  try {
    const currentValue = window.localStorage.getItem(STORAGE_KEY);

    if (currentValue) {
      return JSON.parse(currentValue) as Record<number, SplitPaymentStates>;
    }
  } catch {}

  return {};
};

const splitPaymentOrdersStateAtom = atomWithStorage<{
  [orderId: number]: SplitPaymentStates;
}>(STORAGE_KEY, getInitialStoredSplitPaymentState());

export const useReadSplitPaymentOrdersState = () => {
  const { orderId } = usePaymentLedgerHook(false);
  const splitPaymentOrdersState = useAtomValue(splitPaymentOrdersStateAtom);

  return splitPaymentOrdersState[orderId] ?? initialSplitState;
};

export const useSetSplitPaymentOrdersState = () => {
  const { orderId } = usePaymentLedgerHook(false);
  const splitPaymentOrdersState = useReadSplitPaymentOrdersState();
  const addSplitPaymentState = useAddSplitPaymentState();

  return (data: Partial<SplitPaymentStates>) => {
    const newData = { ...splitPaymentOrdersState, ...data };
    addSplitPaymentState(orderId, newData);
  };
};

export const useDeleteSplitPaymentState = () => {
  const splitPaymentOrdersState = useAtomValue(splitPaymentOrdersStateAtom);
  const setSplitPaymentOrdersState = useSetAtom(splitPaymentOrdersStateAtom);

  return (
    orderId: number,
    currentSplitPaymentOrdersState?: {
      [orderId: number]: SplitPaymentStates;
    }
  ) => {
    const orderState = splitPaymentOrdersState[orderId];

    if (orderState) {
      const clonedState = {
        ...(currentSplitPaymentOrdersState ?? splitPaymentOrdersState),
      };
      delete splitPaymentOrdersState[orderId];
      setSplitPaymentOrdersState(clonedState);
    }
  };
};

export const useAddSplitPaymentState = () => {
  const splitPaymentOrdersState = useAtomValue(splitPaymentOrdersStateAtom);
  const setSplitPaymentOrdersState = useSetAtom(splitPaymentOrdersStateAtom);

  const deleteSplitState = useDeleteSplitPaymentState();

  return (orderId: number, data: SplitPaymentStates) => {
    const currentSplitPaymentOrdersState = {
      ...splitPaymentOrdersState,
      [orderId]: data,
    };
    const stateCount = Object.keys(currentSplitPaymentOrdersState).length;
    if (stateCount > 5) {
      const orderIds = Object.keys(splitPaymentOrdersState);
      const orderIdToBeDeleted = Math.min(...orderIds.map(Number));
      deleteSplitState(orderIdToBeDeleted, currentSplitPaymentOrdersState);
    } else {
      setSplitPaymentOrdersState(currentSplitPaymentOrdersState);
    }
  };
};

const initialSelectedCartItems: SelectedCartItem[] = [];
const selectedCartItemsAtom = atom<SelectedCartItem[]>(
  initialSelectedCartItems
);
export const useSelectedCartItems = () => useAtom(selectedCartItemsAtom);
export const useSetSelectedCartItems = () => useSetAtom(selectedCartItemsAtom);

export const useResetSplitPaymentAtoms = () => {
  const resetSelectedCartItems = useSetSelectedCartItems();
  const resetSplitPaymentStates = useSetSplitPaymentOrdersState();

  return () => {
    resetSelectedCartItems(initialSelectedCartItems);
    /*  resetSplitPaymentStates(initialSplitState); */
  };
};
